﻿package org.zq.pixelset.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Link
import androidx.compose.material.icons.filled.LinkOff
import androidx.compose.material.icons.outlined.Image
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Slider
import androidx.compose.material3.SliderDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.zq.pixelset.theme.PixelSetColors

@Composable
fun SectionTitle(icon: @Composable () -> Unit, title: String) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        icon()
        PixelSetText(
            text = title,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = PixelSetColors.TextPrimary,
        )
    }
}

@Composable
fun SegmentedControl(
    options: List<String>,
    selectedIndex: Int,
    onSelected: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(PixelSetColors.Border)
            .padding(3.dp),
    ) {
        options.forEachIndexed { index, label ->
            val selected = index == selectedIndex
            Box(
                modifier = Modifier
                    .weight(1f)
                    .clip(RoundedCornerShape(6.dp))
                    .background(if (selected) PixelSetColors.Surface else Color.Transparent)
                    .clickable { onSelected(index) }
                    .padding(vertical = 8.dp),
                contentAlignment = Alignment.Center,
            ) {
                PixelSetText(
                    text = label,
                    fontSize = 13.sp,
                    fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                    color = if (selected) PixelSetColors.Primary else PixelSetColors.TextSecondary,
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PixelDropdown(
    label: String,
    options: List<String>,
    selectedIndex: Int,
    onSelected: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    var expanded by remember { mutableStateOf(false) }
    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = { expanded = it },
        modifier = modifier,
    ) {
        OutlinedTextField(
            value = options.getOrElse(selectedIndex) { "" },
            onValueChange = {},
            readOnly = true,
            label = { PixelSetText(label) },
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded) },
            modifier = Modifier.menuAnchor().fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
        )
        ExposedDropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            options.forEachIndexed { index, option ->
                DropdownMenuItem(
                    text = { PixelSetText(option) },
                    onClick = {
                        onSelected(index)
                        expanded = false
                    },
                )
            }
        }
    }
}

@Composable
fun DimensionInput(
    label: String,
    value: Int,
    onValueChange: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier) {
        PixelSetText(
            text = label,
            fontSize = 12.sp,
            color = PixelSetColors.TextMuted,
            modifier = Modifier.padding(bottom = 4.dp),
        )
        OutlinedTextField(
            value = value.toString(),
            onValueChange = { text ->
                text.filter { it.isDigit() }.toIntOrNull()?.let(onValueChange)
            },
            singleLine = true,
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
        )
    }
}

@Composable
fun AspectRatioLockButton(
    locked: Boolean,
    onToggle: () -> Unit,
) {
    IconButton(
        onClick = onToggle,
        modifier = Modifier
            .size(36.dp)
            .clip(CircleShape)
            .background(if (locked) PixelSetColors.PrimaryLight else PixelSetColors.Border),
    ) {
        Icon(
            imageVector = if (locked) Icons.Default.Link else Icons.Default.LinkOff,
            contentDescription = if (locked) "锁定比例" else "解锁比例",
            tint = if (locked) PixelSetColors.Primary else PixelSetColors.TextMuted,
            modifier = Modifier.size(18.dp),
        )
    }
}

@Composable
fun ParamSlider(
    label: String,
    value: Float,
    valueLabel: String,
    onValueChange: (Float) -> Unit,
    valueRange: ClosedFloatingPointRange<Float>,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        PixelSetText(label, fontSize = 13.sp, color = PixelSetColors.TextSecondary)
        PixelSetText(
            valueLabel,
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
            color = PixelSetColors.Primary,
        )
    }
    Slider(
        value = value,
        onValueChange = onValueChange,
        valueRange = valueRange,
        colors = SliderDefaults.colors(
            thumbColor = PixelSetColors.Primary,
            activeTrackColor = PixelSetColors.Primary,
            inactiveTrackColor = PixelSetColors.Border,
        ),
    )
}

@Composable
fun QualitySlider(
    quality: Float,
    onQualityChange: (Float) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        PixelSetText("压缩质量", fontSize = 13.sp, color = PixelSetColors.TextSecondary)
        PixelSetText(
            "${(quality * 100).toInt()}%",
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
            color = PixelSetColors.Primary,
        )
    }
    Slider(
        value = quality,
        onValueChange = onQualityChange,
        valueRange = 0.1f..1f,
        colors = SliderDefaults.colors(
            thumbColor = PixelSetColors.Primary,
            activeTrackColor = PixelSetColors.Primary,
            inactiveTrackColor = PixelSetColors.Border,
        ),
    )
}

@Composable
fun FileThumbnailPlaceholder() {
    Box(
        modifier = Modifier
            .size(40.dp)
            .clip(RoundedCornerShape(6.dp))
            .background(PixelSetColors.PrimaryLight),
        contentAlignment = Alignment.Center,
    ) {
        Icon(
            Icons.Outlined.Image,
            contentDescription = null,
            tint = PixelSetColors.Primary,
            modifier = Modifier.size(22.dp),
        )
    }
}

@Composable
fun Badge(
    text: String,
    backgroundColor: Color = PixelSetColors.PrimaryLight,
    textColor: Color = PixelSetColors.Primary,
) {
    PixelSetText(
        text = text,
        modifier = Modifier
            .clip(RoundedCornerShape(12.dp))
            .background(backgroundColor)
            .padding(horizontal = 8.dp, vertical = 2.dp),
        fontSize = 12.sp,
        fontWeight = FontWeight.Medium,
        color = textColor,
    )
}

@Composable
fun PlaceholderBox(text: String, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(12.dp))
            .background(PixelSetColors.SurfaceVariant)
            .border(1.dp, PixelSetColors.Border, RoundedCornerShape(12.dp)),
        contentAlignment = Alignment.Center,
    ) {
        PixelSetText(
            text = text,
            color = PixelSetColors.TextMuted,
            fontSize = 14.sp,
            textAlign = TextAlign.Center,
            modifier = Modifier.padding(24.dp),
        )
    }
}
